import type { UnitCode } from "@/api/types";

/**
 * Affichage des valeurs physiques (TICKET PM-01).
 *
 * Le serveur stocke en milligrammes et en millimètres — une unité canonique
 * unique, parce que le métrique et l'impérial cohabitent sur la même étagère
 * dans ce métier et qu'un nombre sans unité y est ambigu.
 *
 * Ce module convertit pour l'œil, et seulement pour l'œil. Rien de ce qu'il
 * produit ne repart vers le serveur : la saisie envoie toujours une valeur
 * *avec son unité*, et c'est le backend qui canonise.
 */

/** Combien de milligrammes vaut une unité de masse. */
const MASS_IN_MG: Partial<Record<UnitCode, number>> = {
  G: 1_000,
  KG: 1_000_000,
  OZ: 28_349.523125,
  LB: 453_592.37,
};

/** Combien de millimètres vaut une unité de longueur. */
const LENGTH_IN_MM: Partial<Record<UnitCode, number>> = {
  MM: 1,
  CM: 10,
  M: 1_000,
  IN: 25.4,
  FT: 304.8,
};

export const MASS_UNITS: UnitCode[] = ["G", "KG", "OZ", "LB"];
export const LENGTH_UNITS: UnitCode[] = ["MM", "CM", "M", "IN", "FT"];

/** Unités qu'un produit peut réellement avoir comme unité de stock. */
export const STOCKABLE_UNITS: UnitCode[] = [
  "EA",
  "MM",
  "CM",
  "M",
  "IN",
  "FT",
  "G",
  "KG",
  "OZ",
  "LB",
  "ML",
  "L",
  "GAL",
];

/**
 * Un poids, dans l'unité la plus lisible.
 *
 * 450 g reste en grammes ; 2 310 000 mg devient 2,31 kg. Personne ne lit un
 * poids en milligrammes, et forcer une unité fixe donnerait « 0,00045 kg ».
 */
export function formatWeight(milligrams: number | null): string {
  if (milligrams === null) return "—";
  if (milligrams >= 1_000_000) return `${decimal(milligrams / 1_000_000, 3)} kg`;
  if (milligrams >= 1_000) return `${decimal(milligrams / 1_000, 2)} g`;
  return `${milligrams} mg`;
}

/**
 * Un nombre à virgule, avec la virgule.
 *
 * `toFixed` écrit « 2.184 » ; le reste de l'interface écrit « 37,5 ». Deux
 * conventions décimales sur le même écran font douter du chiffre lui-même.
 */
function decimal(value: number, places: number): string {
  return value
    .toFixed(places)
    .replace(/\.?0+$/, "")
    .replace(".", ",");
}

/** Une longueur, en millimètres ou en mètres selon l'ordre de grandeur. */
export function formatLength(millimetres: string | number | null): string {
  if (millimetres === null) return "—";
  const value = typeof millimetres === "string" ? Number(millimetres) : millimetres;
  if (!Number.isFinite(value)) return "—";
  if (value >= 1_000) return `${decimal(value / 1_000, 3)} m`;
  return `${decimal(value, 1)} mm`;
}

/** L × l × h, ou rien du tout si aucune n'est renseignée. */
export function formatDimensions(
  length: string | number | null,
  width: string | number | null,
  height: string | number | null,
): string | null {
  if (length === null && width === null && height === null) return null;
  return [length, width, height].map((value) => formatLength(value)).join(" × ");
}

export function toMilligrams(value: number, unit: UnitCode): number | null {
  const ratio = MASS_IN_MG[unit];
  return ratio === undefined ? null : Math.round(value * ratio);
}

export function toMillimetres(value: number, unit: UnitCode): number | null {
  const ratio = LENGTH_IN_MM[unit];
  return ratio === undefined ? null : value * ratio;
}
